import { Component, OnInit, inject, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';

import { PropertyInsuranceService, PropertyInsurance, COVERAGE_TYPE_LABELS } from 'src/shared/services/property-insurance.service';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';

import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  templateUrl: 'add-edit-insurance.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    ReactiveFormsModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AddEditInsurancePage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private modalCtrl = inject(ModalController);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private client = inject(Client);
  private propertyInsuranceService = inject(PropertyInsuranceService);

  form: FormGroup;
  submitted = false;
  loaded = false;
  isRightSideContent = false;
  errorMessage: string;
  insurance: PropertyInsurance;
  isEditMode = false;
  addressItems: TidySelectNumberValueModel[] = [];
  coverageTypeItems: TidySelectNumberValueModel[] = [];
  autoRenew = false;
  countryCode: string;

  boundSave = this.save.bind(this);
  boundDelete = this.deleteInsurance.bind(this);

  async ngOnInit() {
    this.buildCoverageTypeItems();
    this.form = this.fb.group({
      address_id: [null, Validators.required],
      provider_name: ['', Validators.required],
      // The API declares `requires :policy_number` and the model validates its presence,
      // so a blank one is rejected server-side — catch it here instead.
      policy_number: ['', Validators.required],
      coverage_type: [null, Validators.required],
      coverage_amount: [null],
      annual_premium_amount: [null],
      deductible_amount: [null],
      effective_date: [''],
      expiration_date: [''],
      agent_name: [''],
      agent_phone: [''],
    });

    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const dialogParams = await this.storage.retrieve('dialog-params');
    // Params arrive via router state on mobile and via dialog-params on desktop.
    const insurance = this.navCtrl.getParam('insurance') || dialogParams?.insurance;
    const preselectedAddressId = this.navCtrl.getParam('addressId') || dialogParams?.addressId;
    if (insurance) {
      this.insurance = insurance;
      this.isEditMode = true;
    }

    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle(this.isEditMode ? 'Insurance Policy' : 'Add Policy');
    }

    try {
      await this.loadAddresses();
      if (this.isEditMode) {
        this.fillForm();
      } else if (preselectedAddressId) {
        this.form.patchValue({ address_id: preselectedAddressId });
      }
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load data';
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  async loadAddresses() {
    const addresses = await this.client.getAddresses();
    this.addressItems = this.client.parseAddressList(addresses);
  }

  buildCoverageTypeItems() {
    this.coverageTypeItems = Object.entries(COVERAGE_TYPE_LABELS).map(([value, viewValue]) => ({
      viewValue,
      value: value as any,
    }));
  }

  fillForm() {
    this.autoRenew = this.insurance.auto_renew || false;
    this.form.patchValue({
      address_id: this.insurance.address?.id,
      provider_name: this.insurance.provider_name || '',
      policy_number: this.insurance.policy_number || '',
      coverage_type: this.insurance.coverage_type || null,
      coverage_amount: this.insurance.coverage_amount !== null ? this.insurance.coverage_amount / 100 : null,
      annual_premium_amount: this.insurance.annual_premium_amount !== null ? this.insurance.annual_premium_amount / 100 : null,
      deductible_amount: this.insurance.deductible_amount !== null ? this.insurance.deductible_amount / 100 : null,
      effective_date: this.insurance.effective_date || '',
      expiration_date: this.insurance.expiration_date || '',
      agent_name: this.insurance.agent_name || '',
      agent_phone: this.insurance.agent_phone || '',
    });
  }

  /** A cleared money input holds `''`, which `Math.round('' * 100)` turned into a stored 0. */
  private toCents(value: any): number | null {
    if (value === null || value === undefined || value === '') { return null; }
    const amount = Number(value);
    return isNaN(amount) ? null : Math.round(amount * 100);
  }

  /** Grape declares the dates as `type: Date`, and coercing `''` fails the whole request. */
  private toDate(value: any): string | null {
    return value ? value : null;
  }

  /** `parsePhoneNumber('')` returns a bare '+', so only run it on a real number. */
  private toPhone(value: any): string | null {
    const digits = (value || '').toString().replace(/[^\d]/g, '');
    return digits ? parsePhoneNumber(value) : null;
  }

  async save() {
    this.submitted = true;
    if (this.form.invalid) {
      // Without this the button silently does nothing when a required field is empty.
      this.form.markAllAsTouched();
      this.errorMessage = 'Please fill in the property, provider, policy number, and coverage type.';
      this.cdr.markForCheck();
      return;
    }
    this.errorMessage = null;
    try {
      const formVal = this.form.value;
      const body: Partial<PropertyInsurance> = {
        address_id: formVal.address_id,
        provider_name: formVal.provider_name,
        policy_number: formVal.policy_number,
        coverage_type: formVal.coverage_type,
        coverage_amount: this.toCents(formVal.coverage_amount),
        annual_premium_amount: this.toCents(formVal.annual_premium_amount),
        deductible_amount: this.toCents(formVal.deductible_amount),
        effective_date: this.toDate(formVal.effective_date),
        expiration_date: this.toDate(formVal.expiration_date),
        auto_renew: this.autoRenew,
        agent_name: formVal.agent_name || null,
        agent_phone: this.toPhone(formVal.agent_phone),
      } as Partial<PropertyInsurance>;

      if (this.isEditMode) {
        await this.propertyInsuranceService.updatePropertyInsurance(this.insurance.id, body);
      } else {
        await this.propertyInsuranceService.createPropertyInsurance(body);
      }

      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.back();
      }
    } catch (err) {
      this.errorMessage = this.parseApiError(err, 'Failed to save insurance');
      this.cdr.markForCheck();
    }
  }

  /**
   * Grape validation failures come back as `{ error: 'coverage_type does not have a valid value' }`,
   * which ErrorInterceptor passes through untouched — so the useful message sits at `err.err.error`
   * (a plain string) rather than the `err.error.message` shape the rest of the app expects.
   */
  private parseApiError(err: any, fallback: string): string {
    const candidates = [err?.error?.message, err?.message, err?.err?.error, err?.error];
    const message = candidates.find(value => typeof value === 'string' && value.length > 0);
    return message || fallback;
  }

  async deleteInsurance() {
    const params: ConfirmPageParamsModel = {
      title: 'Delete Insurance Policy?',
      body: 'Are you sure you want to delete this insurance policy? This action cannot be undone.',
      backText: 'Go Back',
      confirmText: 'Delete',
      confirmAction: this.confirmDelete.bind(this),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await confirmationModal.present();
  }

  async confirmDelete() {
    try {
      await this.propertyInsuranceService.deletePropertyInsurance(this.insurance.id);
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      this.modalCtrl.dismiss();
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.back();
      }
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to delete insurance';
      this.cdr.markForCheck();
    }
  }

  onCountryCodeChange(countryCode: string) {
    this.countryCode = countryCode.replace('+', '');
  }

  toggleAutoRenew() {
    this.autoRenew = !this.autoRenew;
  }
}
